import { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { BaseChart } from './BaseChart.js';
import { formatCurrency, formatNumber } from '../../utils/dataFormatters.js';
import { generateChartAriaLabel } from '../../utils/accessibility.js';
import type { SalesPointPerformanceChartProps } from '../../types/analytics.types.js';
import { SalesPointTag } from '../../../transactions/components/SalesPointTag/SalesPointTag.js';
import { buildSalesPointColorIndexMap } from '../../../transactions/domain/salesPointTagColors.js';

/**
 * Production-grade sales point performance chart with proper labels, sorting, interactions, and accessibility
 */
export const SalesPointPerformanceChart = memo<SalesPointPerformanceChartProps>(
  ({
    data,
    sortBy = 'revenue',
    isLoading,
    error,
    emptyMessage,
    emptyHint,
    title,
    subtitle,
    'aria-label': ariaLabel,
    'aria-describedby': ariaDescribedBy,
  }) => {
    const { t } = useTranslation('admin');
    const resolvedTitle = title ?? t('analytics.charts.kioskPerformanceTitle');
    const resolvedSubtitle = subtitle ?? t('analytics.charts.kioskPerformanceSubtitle');

    const sortedData = useMemo(() => {
      const sorted = [...data];
      sorted.sort((a, b) => {
        if (sortBy === 'revenue') {
          return b.revenue - a.revenue;
        }
        if (sortBy === 'orders') {
          return b.orders - a.orders;
        }
        if (sortBy === 'refunds') {
          return b.refunds - a.refunds;
        }
        return 0;
      });
      return sorted.slice(0, 10);
    }, [data, sortBy]);

    const salesPointColorIndexMap = useMemo(
      () => buildSalesPointColorIndexMap(sortedData.map((sp) => sp.salesPointId)),
      [sortedData],
    );

    const maxRevenue = useMemo(() => {
      return Math.max(...sortedData.map((d) => d.revenue), 0);
    }, [sortedData]);

    const chartAriaLabel = useMemo(
      () => ariaLabel || generateChartAriaLabel(resolvedTitle, sortedData.length, resolvedSubtitle),
      [ariaLabel, resolvedTitle, sortedData.length, resolvedSubtitle]
    );

    return (
      <BaseChart
        isLoading={isLoading}
        error={error}
        emptyMessage={emptyMessage}
        emptyHint={emptyHint}
        title={resolvedTitle}
        subtitle={resolvedSubtitle}
        aria-label={chartAriaLabel}
        aria-describedby={ariaDescribedBy}
      >
        {sortedData.length > 0 ? (
          <div className="flex flex-col gap-4 max-md:gap-3" role="img" aria-label={chartAriaLabel}>
            <div className="flex flex-col gap-3">
              {sortedData.map((salesPoint) => {
                const salesPointName =
                  salesPoint.salesPointName ||
                  t('analytics.charts.kioskNameFallback', { id: salesPoint.salesPointId });
                return (
                  <div
                    key={salesPoint.salesPointId}
                    className="flex flex-col gap-1.5 cursor-pointer transition-transform p-2 rounded-lg hover:bg-[var(--color-an-surface-hover)] hover:translate-x-1 focus:outline focus:outline-2 focus:outline-[var(--color-an-primary)] max-md:min-h-11 max-md:p-3"
                    role="group"
                    aria-label={t('analytics.charts.kioskPointAria', {
                      name: salesPointName,
                      revenue: formatCurrency(salesPoint.revenue),
                      orders: formatNumber(salesPoint.orders),
                    })}
                  >
                    <div className="mb-1 text-sm font-semibold text-[var(--color-an-text)] max-md:text-[13px]">
                      <SalesPointTag
                        paletteIndex={
                          salesPointColorIndexMap.get(salesPoint.salesPointId) ??
                          salesPoint.salesPointId
                        }
                      >
                        {salesPointName}
                      </SalesPointTag>
                    </div>
                    <div className="w-full h-8 max-[480px]:h-7 bg-[var(--color-an-surface-muted,var(--color-an-bg))] rounded-md overflow-hidden relative">
                      <div
                        className="h-full rounded-md flex items-center justify-end px-3 transition-[width] min-w-[80px] max-md:min-w-[60px]"
                        style={{
                          width: `${maxRevenue > 0 ? (salesPoint.revenue / maxRevenue) * 100 : 0}%`,
                          background: 'var(--color-an-chart-2, var(--color-an-success))',
                        }}
                        aria-label={t('analytics.charts.revenueAria', {
                          value: formatCurrency(salesPoint.revenue),
                        })}
                      >
                        <span className="text-xs font-bold text-white whitespace-nowrap max-md:text-[11px]">
                          {formatCurrency(salesPoint.revenue)}
                        </span>
                      </div>
                    </div>
                    <div className="flex gap-3 text-xs text-[var(--color-an-text-muted)]">
                      <span>
                        {t('analytics.charts.kioskOrdersCount', { count: formatNumber(salesPoint.orders) })}
                      </span>
                      {salesPoint.refunds > 0 ? (
                        <span className="font-semibold text-[var(--color-an-warn,#d97706)]">
                          {t('analytics.charts.kioskRefundsCount', {
                            count: formatNumber(salesPoint.refunds),
                          })}
                        </span>
                      ) : null}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : null}
      </BaseChart>
    );
  }
);

SalesPointPerformanceChart.displayName = 'SalesPointPerformanceChart';
